import React, { ReactNode } from "react";
import { cls } from "@wss/cls";
import UseOurLiveChat from "../../UseOurLiveChat/UseOurLiveChat";
import useIsMounted from "~/common/useIsMounted";
import HelpEmail from "~/common/HelpEmail";
import HelpFrequentlyAskedQuestions from "~/common/HelpFrequentlyAskedQuestions";
import Store, { Provider } from "~/common/Store";
import HelpPdfQuote from "~/common/HelpPdfQuote";
import HelpPhone from "~/common/HelpPhone";
import HelpCartId from "~/common/HelpCartId";
import HelpSpanish from "~/common/HelpSpanish";

interface Props {
  helpBlockSections?: HelpBlockSectionName[];
  liveChatClassNames?: string;
  helpBlockClassNames?: string;
  borderNone?: boolean;
}

export type HelpBlockSectionName =
  | "LiveChat"
  | "Email"
  | "FrequentlyAskedQuestions"
  | "PdfQuote"
  | "Phone"
  | "CartId"
  | "Spanish";

const HelpBlock = ({
  helpBlockSections,
  liveChatClassNames,
  helpBlockClassNames = "lt:mx-0 mx-3",
  borderNone = true,
}: Props) => {
  const mounted = useIsMounted();

  const displaySections = [
    ...new Set<HelpBlockSectionName>(
      helpBlockSections || ["LiveChat", "Email"],
    ),
  ];

  const sectionMap: Map<HelpBlockSectionName, () => ReactNode> = new Map([
    [
      "LiveChat",
      (): ReactNode => (
        <UseOurLiveChat
          mounted={mounted}
          toggleBorderOff
          className={
            liveChatClassNames ? cls(liveChatClassNames) : cls("mx-4 mt-4")
          }
        />
      ),
    ],
    ["Email", (): ReactNode => <HelpEmail mounted={mounted} />],
    [
      "FrequentlyAskedQuestions",
      (): ReactNode => <HelpFrequentlyAskedQuestions />,
    ],
    ["PdfQuote", (): ReactNode => <HelpPdfQuote />],
    ["Phone", (): ReactNode => <HelpPhone />],
    ["CartId", (): ReactNode => <HelpCartId mounted={mounted} />],
    ["Spanish", (): ReactNode => <HelpSpanish mounted={mounted} />],
  ]);

  return (
    <div data-module="initHelpBlock">
      <Provider store={Store}>
        <div
          className={cls(
            "help",
            "help--vertical",
            "rounded",
            borderNone && "border-none",
            helpBlockClassNames,
          )}
        >
          {[...displaySections].map((section: HelpBlockSectionName) => {
            const sectionNode = sectionMap.get(section);
            if (sectionNode) {
              return (
                <React.Fragment key={section}>{sectionNode()}</React.Fragment>
              );
            }

            return null;
          })}
        </div>
      </Provider>
    </div>
  );
};

export default HelpBlock;
